/**
 * Project Image Generator
 * Generate custom project images using Canvas API
 */

/**
 * Generate a Base64 Util project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateBase64UtilImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // Gradient background
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#667eea');
  gradient.addColorStop(0.5, '#764ba2');
  gradient.addColorStop(1, '#f093fb');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Add overlay pattern
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  for (let i = 0; i < 20; i++) {
    for (let j = 0; j < 12; j++) {
      if ((i + j) % 2) {
        ctx.fillRect(i * 20, j * 20, 10, 10);
      }
    }
  }

  // Central design
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillRect(50, 50, 300, 150);
  
  // Border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 2;
  ctx.strokeRect(50, 50, 300, 150);

  // Text styling
  ctx.fillStyle = '#2d3748';
  ctx.font = 'bold 24px Arial, sans-serif';
  ctx.textAlign = 'center';
  
  // Title
  ctx.fillText('Base64', 200, 100);
  ctx.fillText('Utility', 200, 130);
  
  // Subtitle
  ctx.font = '14px Arial, sans-serif';
  ctx.fillStyle = '#4a5568';
  ctx.fillText('File Converter & Encoder', 200, 155);
  
  // Base64 sample text
  ctx.font = '10px monospace';
  ctx.fillStyle = '#718096';
  ctx.fillText('SGVsbG8gV29ybGQ=', 200, 175);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Generate a JSON Util project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateJsonUtilImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // JSON-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#11998e');
  gradient.addColorStop(0.5, '#38ef7d');
  gradient.addColorStop(1, '#3eadcf');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Central container
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillRect(30, 30, 340, 190);
  
  // Border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 2;
  ctx.strokeRect(30, 30, 340, 190);

  // JSON brackets design
  ctx.fillStyle = '#2d3748';
  ctx.font = 'bold 60px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('{', 100, 140);
  ctx.fillText('}', 300, 140);

  // Title
  ctx.font = 'bold 24px Arial, sans-serif';
  ctx.fillText('JSON', 200, 110);
  ctx.fillText('Tools', 200, 140);
  
  // Subtitle
  ctx.font = '14px Arial, sans-serif';
  ctx.fillStyle = '#4a5568';
  ctx.fillText('Beautify • Validate • Compare', 200, 170);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Save generated image to file (for development)
 * @param {string} base64Image - The base64 image data
 * @param {string} filename - Filename to save as
 */
export const saveGeneratedImage = (base64Image, filename) => {
  const link = document.createElement('a');
  link.download = filename;
  link.href = base64Image;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Generate a portfolio website project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generatePortfolioImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // Portfolio-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#667eea');
  gradient.addColorStop(0.5, '#764ba2');
  gradient.addColorStop(1, '#f093fb');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Add geometric pattern
  ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
  for (let i = 0; i < 8; i++) {
    ctx.beginPath();
    ctx.arc(50 + i * 40, 50, 15, 0, Math.PI * 2);
    ctx.fill();
  }

  // Central container
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillRect(50, 60, 300, 130);
  
  // Border
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.lineWidth = 2;
  ctx.strokeRect(50, 60, 300, 130);

  // Title
  ctx.fillStyle = '#2d3748';
  ctx.font = 'bold 24px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Portfolio', 200, 110);
  ctx.fillText('Website', 200, 140);
  
  // Subtitle
  ctx.font = '14px Arial, sans-serif';
  ctx.fillStyle = '#4a5568';
  ctx.fillText('Vue 3 • Modern Design', 200, 165);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Generate a React dashboard project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateReactDashboardImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // Dashboard-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#1e3c72');
  gradient.addColorStop(0.5, '#2a5298');
  gradient.addColorStop(1, '#00b4db');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Dashboard layout mockup
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillRect(30, 30, 340, 190);
  
  // Header bar
  ctx.fillStyle = '#2d3748';
  ctx.fillRect(30, 30, 340, 40);
  
  // Chart areas
  ctx.fillStyle = '#f7fafc';
  ctx.fillRect(50, 90, 120, 60);
  ctx.fillRect(190, 90, 120, 60);
  ctx.fillRect(50, 170, 260, 40);

  // Chart data visualization
  ctx.strokeStyle = '#4299e1';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(60, 140);
  ctx.lineTo(80, 120);
  ctx.lineTo(100, 110);
  ctx.lineTo(120, 130);
  ctx.lineTo(140, 100);
  ctx.stroke();

  // Title
  ctx.fillStyle = 'white';
  ctx.font = 'bold 14px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('React Dashboard', 200, 55);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Generate a mobile app project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateMobileAppImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // Mobile-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#ff9a9e');
  gradient.addColorStop(0.5, '#fecfef');
  gradient.addColorStop(1, '#fecfef');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Phone mockup
  ctx.fillStyle = '#2d3748';
  ctx.fillRect(150, 40, 100, 170);
  ctx.fillRect(155, 50, 90, 150);
  
  // Screen
  ctx.fillStyle = 'white';
  ctx.fillRect(160, 55, 80, 140);
  
  // App interface mockup
  ctx.fillStyle = '#4299e1';
  ctx.fillRect(165, 60, 70, 20);
  
  ctx.fillStyle = '#f7fafc';
  ctx.fillRect(165, 90, 70, 15);
  ctx.fillRect(165, 110, 70, 15);
  ctx.fillRect(165, 130, 70, 15);

  // Home button
  ctx.fillStyle = '#2d3748';
  ctx.beginPath();
  ctx.arc(200, 220, 8, 0, Math.PI * 2);
  ctx.fill();

  // Title
  ctx.fillStyle = '#2d3748';
  ctx.font = 'bold 18px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Mobile App', 200, 30);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Generate an API service project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateApiServiceImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // API-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#134e5e');
  gradient.addColorStop(0.5, '#71b280');
  gradient.addColorStop(1, '#134e5e');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Terminal/console mockup
  ctx.fillStyle = '#1a202c';
  ctx.fillRect(50, 50, 300, 150);
  
  // Terminal header
  ctx.fillStyle = '#2d3748';
  ctx.fillRect(50, 50, 300, 30);
  
  // Terminal dots
  ctx.fillStyle = '#ff5f56';
  ctx.beginPath();
  ctx.arc(70, 65, 5, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.fillStyle = '#ffbd2e';
  ctx.beginPath();
  ctx.arc(90, 65, 5, 0, Math.PI * 2);
  ctx.fill();
  
  ctx.fillStyle = '#27ca3f';
  ctx.beginPath();
  ctx.arc(110, 65, 5, 0, Math.PI * 2);
  ctx.fill();

  // API endpoints text
  ctx.fillStyle = '#68d391';
  ctx.font = '12px monospace';
  ctx.textAlign = 'left';
  ctx.fillText('GET /api/users', 70, 110);
  ctx.fillText('POST /api/auth', 70, 130);
  ctx.fillText('PUT /api/data', 70, 150);
  ctx.fillText('DELETE /api/cache', 70, 170);

  // Status indicators
  ctx.fillStyle = '#27ca3f';
  ctx.fillText('200', 300, 110);
  ctx.fillText('201', 300, 130);
  ctx.fillText('200', 300, 150);
  ctx.fillText('204', 300, 170);

  return canvas.toDataURL('image/jpeg', 0.9);
};

/**
 * Generate a QR code utility project image
 * @returns {string} Base64 data URL of the generated image
 */
export const generateQrUtilImage = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 400;
  canvas.height = 250;
  const ctx = canvas.getContext('2d');

  // QR-themed gradient
  const gradient = ctx.createLinearGradient(0, 0, 400, 250);
  gradient.addColorStop(0, '#4776E6');
  gradient.addColorStop(1, '#8E54E9');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 400, 250);

  // Central container
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.fillRect(70, 50, 260, 150);
  
  // Draw QR code pattern
  ctx.fillStyle = '#2d3748';
  
  // QR code corner squares
  ctx.fillRect(100, 80, 30, 30);
  ctx.fillRect(210, 80, 30, 30);
  ctx.fillRect(100, 140, 30, 30);
  
  // QR code inner pattern
  const dotSize = 8;
  const startX = 140;
  const startY = 90;
  
  // Create pattern of dots
  for (let i = 0; i < 10; i++) {
    for (let j = 0; j < 8; j++) {
      if ((i + j) % 2 === 0) {
        ctx.fillRect(startX + i * dotSize, startY + j * dotSize, dotSize, dotSize);
      }
    }
  }
  
  // QR frame
  ctx.strokeStyle = '#2d3748';
  ctx.lineWidth = 2;
  ctx.strokeRect(90, 70, 160, 110);
  
  // Title
  ctx.fillStyle = '#2d3748';
  ctx.font = 'bold 20px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('QR Code', 320, 110);
  ctx.fillText('Utility', 320, 135);
  
  // Subtitle
  ctx.font = '12px Arial, sans-serif';
  ctx.fillStyle = '#4a5568';
  ctx.fillText('Generate & Scan', 320, 160);

  return canvas.toDataURL('image/jpeg', 0.9);
};

export default {
  generateBase64UtilImage,
  generateJsonUtilImage,
  generatePortfolioImage,
  generateReactDashboardImage,
  generateMobileAppImage,
  generateApiServiceImage,
  generateQrUtilImage,
  saveGeneratedImage
};
